import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: t('nav.home'), testKey: 'home' },
    { to: '/appartamenti', label: t('nav.apartments'), testKey: 'apartments' },
    { to: '/location', label: t('nav.location'), testKey: 'location' },
    { to: '/galleria', label: t('nav.gallery'), testKey: 'gallery' },
    { to: '/contatti', label: t('nav.contact'), testKey: 'contact' }
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'backdrop-blur-xl bg-white/70 border-b border-white/20 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link
            to="/"
            data-testid="header-logo-link"
            className="text-2xl font-bold text-[#2C5263] font-serif tracking-tight"
          >
            Cavallino Treporti
          </Link>

          <nav className="hidden md:flex items-center gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.testKey}`}
                className={`text-sm font-medium transition-colors ${
                  location.pathname === link.to
                    ? 'text-[#2C5263]'
                    : 'text-[#666666] hover:text-[#2C5263]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <LanguageSwitcher />
            <Link
              to="/prenota"
              data-testid="header-book-now-button"
              className="bg-[#2C5263] text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-[#1A3644] transition-colors"
            >
              {t('nav.bookNow')}
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-[#2C5263] p-2"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white border-t border-gray-200"
        >
          <nav className="flex flex-col p-4 gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-link-${link.testKey}`}
                onClick={() => setIsMobileMenuOpen(false)}
                className={`text-base font-medium transition-colors ${
                  location.pathname === link.to
                    ? 'text-[#2C5263]'
                    : 'text-[#666666] hover:text-[#2C5263]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="py-2">
              <LanguageSwitcher />
            </div>
            <Link
              to="/prenota"
              data-testid="mobile-book-now-button"
              onClick={() => setIsMobileMenuOpen(false)}
              className="bg-[#2C5263] text-white px-6 py-3 rounded-md text-base font-medium hover:bg-[#1A3644] transition-colors text-center"
            >
              {t('nav.bookNow')}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
